import type { ReactNode } from 'react';
import type { FileDiff } from '../shared/types/api';
import { Empty } from './Ui';
import { Icon } from './Icon';

const LABEL = {
  added: 'Added',
  modified: 'Modified',
  deleted: 'Deleted',
} as const;

export function DiffView({
  files,
  truncated,
  footer,
}: {
  files: FileDiff[];
  truncated: boolean;
  /** Extra content under a file's changes, such as the comments about that page. */
  footer?: (file: FileDiff) => ReactNode;
}) {
  if (files.length === 0) return <Empty title="No changes between these revisions" />;
  return (
    <div>
      {files.map((file) => (
        <section className="card diff-file" key={file.path} aria-label={`Changes to ${file.path}`}>
          <div className="card-head">
            <Icon name="file" />
            <span className="path">{file.path}</span>
            <span className="badge">{LABEL[file.status]}</span>
            {file.conflict && (
              <span className="badge conflict" title="This page was changed differently in the other wiki">
                <Icon name="alert" />
                Conflict
              </span>
            )}
            <span className="diff-stat right">
              <span className="add">+{file.additions}</span> <span className="del">−{file.deletions}</span>
            </span>
          </div>
          {file.coarse && (
            <div className="alert warn" style={{ margin: 12 }}>
              This file is large, so the changed region is shown without line-by-line matching.
            </div>
          )}
          <div
            className="diff-wrap"
            tabIndex={0}
            role="region"
            aria-label={`Scrollable diff for ${file.path}`}
          >
            <table className="diff">
              <caption className="sr-only">
                Line changes in {file.path}; plus signs are additions and minus signs are removals.
              </caption>
              <tbody>
                {file.hunks.map((hunk, h) => (
                  <HunkRows key={h} hunk={hunk} />
                ))}
              </tbody>
            </table>
          </div>
          {footer?.(file)}
        </section>
      ))}
      {truncated && <div className="alert warn">This revision changes more files than are shown here.</div>}
    </div>
  );
}

function HunkRows({ hunk }: { hunk: FileDiff['hunks'][number] }) {
  return (
    <>
      <tr className="hunk">
        <td colSpan={4}>
          @@ −{hunk.oldStart},{hunk.oldLines} +{hunk.newStart},{hunk.newLines} @@
        </td>
      </tr>
      {hunk.lines.map((line, i) => (
        <tr className={line.kind} key={i}>
          <td className="num" aria-hidden="true">
            {line.oldLine ?? ''}
          </td>
          <td className="num" aria-hidden="true">
            {line.newLine ?? ''}
          </td>
          <td
            className="num"
            aria-label={line.kind === 'add' ? 'added' : line.kind === 'remove' ? 'removed' : 'unchanged'}
          >
            {line.kind === 'add' ? '+' : line.kind === 'remove' ? '−' : ' '}
          </td>
          <td>{line.text}</td>
        </tr>
      ))}
    </>
  );
}
